import type AuthenticationContext from './AuthenticationContext'
import authorizationHeaders from './authorizationHeaders'
import requireAuthContext from './requireAuthContext'

type UrlResolver = string | ((authContext: AuthenticationContext) => string)

const fetchWithAuthContext = (
  url: UrlResolver,
  init: RequestInit | undefined,
  authContext: AuthenticationContext
): Promise<Response> =>
  fetch(typeof url === 'function' ? url(authContext) : url, {
    ...init,
    headers: {
      ...init?.headers,
      ...authorizationHeaders(authContext),
    },
  })

/**
 * A 401 is retried once with a newly renewed token: tokens live 60s, so one can pass the
 * local expiry check and still expire before the server validates it. The retry resends
 * `init.body`, which must therefore be replayable — a string or a buffer, not a stream.
 * A 401 that survives the retry is a real authorization answer, left to the caller.
 */
const fetchAuthorized = async (url: UrlResolver, init?: RequestInit): Promise<Response> => {
  const authContext = await requireAuthContext()
  const response = await fetchWithAuthContext(url, init, authContext)
  if (response.status !== 401) return response

  return fetchWithAuthContext(url, init, await requireAuthContext({ staleAccessToken: authContext.accessToken }))
}

export default fetchAuthorized
